import { useState, useEffect, useCallback, useRef } from "react";
import * as signalR from "@microsoft/signalr";

// Strip /api suffix — SignalR hubs are at /hubs/*, not /api/hubs/*
const EDITOR_API_URL = (import.meta.env.VITE_EDITOR_API_URL || "").replace(/\/api\/?$/, "");

export interface UserPresence {
  userId: string;
  displayName: string;
  avatarUrl?: string;
}

export interface BlockLockInfo {
  userId: string;
  displayName: string;
}

/** A live block change pushed by Ask Lilia while it edits (Phase 3 streaming). */
export interface AiBlockChangedEvent {
  op: "add" | "edit" | "remove" | "reorder";
  id?: string;
  type?: string;
  content?: Record<string, unknown>;
  afterId?: string | null;
  sortOrder?: number;
  ids?: string[];
}

interface UsePresenceOptions {
  documentId: string;
  userId?: string | null;
  displayName?: string | null;
  avatarUrl?: string | null;
  enabled?: boolean;
  accessTokenFactory?: () => Promise<string | undefined>;
  /** Live AI edits streamed over the same hub connection. */
  onAiBlockChanged?: (evt: AiBlockChangedEvent) => void;
}

interface UsePresenceReturn {
  users: UserPresence[];
  isConnected: boolean;
  lockedBlocks: Map<string, BlockLockInfo>;
  lockBlock: (documentId: string, blockId: string) => void;
  unlockBlock: (documentId: string, blockId: string) => void;
}

export function usePresence({
  documentId,
  userId,
  displayName,
  avatarUrl,
  enabled = true,
  accessTokenFactory,
  onAiBlockChanged,
}: UsePresenceOptions): UsePresenceReturn {
  const aiCbRef = useRef(onAiBlockChanged);
  useEffect(() => { aiCbRef.current = onAiBlockChanged; }, [onAiBlockChanged]);
  const [users, setUsers] = useState<UserPresence[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [lockedBlocks, setLockedBlocks] = useState<Map<string, BlockLockInfo>>(new Map());
  const connectionRef = useRef<signalR.HubConnection | null>(null);

  // Keep latest displayName/avatarUrl in refs so invoke calls see current values
  // without causing the connection to rebuild on every name change.
  const displayNameRef = useRef(displayName);
  const avatarUrlRef = useRef(avatarUrl);
  useEffect(() => { displayNameRef.current = displayName; }, [displayName]);
  useEffect(() => { avatarUrlRef.current = avatarUrl; }, [avatarUrl]);

  // Only reconnect when the document or user identity changes — NOT when
  // display name / avatar update. Those are patched via refs above.
  useEffect(() => {
    if (!enabled || !documentId || !userId) return;

    // Build the connection inside the effect so it is never recreated by
    // a dependency change mid-flight (which caused the "stop() before start()"
    // race when displayName arrived from the server after initial render).
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${EDITOR_API_URL}/hubs/document`, {
        withCredentials: true,
        transport: signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling,
        accessTokenFactory: async () => (await accessTokenFactory?.()) ?? "",
        // Note: SignalR requires a string; undefined falls back to "" which
        // sends an empty bearer — callers must ensure a valid token is returned
        // (see getValidToken in StudioPage for proactive refresh logic).
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connectionRef.current = connection;

    connection.on("CurrentUsers", (currentUsers: UserPresence[]) => {
      setUsers(currentUsers);
    });

    connection.on("UserJoined", (user: UserPresence) => {
      setUsers((prev) => {
        if (prev.some((u) => u.userId === user.userId)) return prev;
        return [...prev, user];
      });
    });

    connection.on("UserLeft", (leftUserId: string) => {
      setUsers((prev) => prev.filter((u) => u.userId !== leftUserId));
      setLockedBlocks((prev) => {
        const next = new Map(prev);
        let changed = false;
        for (const [blockId, lock] of next) {
          if (lock.userId === leftUserId) {
            next.delete(blockId);
            changed = true;
          }
        }
        return changed ? next : prev;
      });
    });

    connection.on("BlockLocked", (blockId: string, lockerUserId: string, lockerDisplayName: string) => {
      setLockedBlocks((prev) => {
        const next = new Map(prev);
        next.set(blockId, { userId: lockerUserId, displayName: lockerDisplayName });
        return next;
      });
    });

    connection.on("AiBlockChanged", (evt: AiBlockChangedEvent) => {
      aiCbRef.current?.(evt);
    });

    connection.on("BlockUnlocked", (blockId: string) => {
      setLockedBlocks((prev) => {
        if (!prev.has(blockId)) return prev;
        const next = new Map(prev);
        next.delete(blockId);
        return next;
      });
    });

    connection.onreconnected(() => {
      setIsConnected(true);
      connection
        .invoke("JoinDocument", documentId, displayNameRef.current ?? "", avatarUrlRef.current ?? "")
        .catch(console.error);
    });

    connection.onclose(() => {
      setIsConnected(false);
    });

    // Track whether cleanup ran before start() resolved
    let stopped = false;

    connection
      .start()
      .then(() => {
        if (stopped) return; // cleanup already ran — don't touch state
        setIsConnected(true);
        return connection.invoke(
          "JoinDocument",
          documentId,
          displayNameRef.current ?? "",
          avatarUrlRef.current ?? ""
        );
      })
      .catch((err) => {
        // "stop() before start()" is benign — swallow it silently
        if (stopped) return;
        console.error("[usePresence] hub start failed:", err);
      });

    return () => {
      stopped = true;
      connectionRef.current = null;
      setUsers([]);
      setIsConnected(false);
      setLockedBlocks(new Map());

      if (connection.state === signalR.HubConnectionState.Connected) {
        connection
          .invoke("LeaveDocument", documentId)
          .catch(() => {})
          .finally(() => connection.stop().catch(() => {}));
      } else {
        connection.stop().catch(() => {});
      }
    };
  // Intentionally excludes displayName/avatarUrl — those update via refs.
  // Only reconnect when document identity or user identity changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, userId, enabled]);

  const lockBlock = useCallback(
    (docId: string, blockId: string) => {
      const connection = connectionRef.current;
      if (connection?.state === signalR.HubConnectionState.Connected) {
        connection.invoke("LockBlock", docId, blockId).catch(console.error);
      }
    },
    []
  );

  const unlockBlock = useCallback(
    (docId: string, blockId: string) => {
      const connection = connectionRef.current;
      if (connection?.state === signalR.HubConnectionState.Connected) {
        connection.invoke("UnlockBlock", docId, blockId).catch(console.error);
      }
    },
    []
  );

  return { users, isConnected, lockedBlocks, lockBlock, unlockBlock };
}
